import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { onPendingChange } from "../lib/offlineSync";
import { useSession } from "../lib/session";

/**
 * The mock's chrome: the brand logos on top, the app title with who is signed in, the screen, and a footer.
 * Also shows a strip when drafts are waiting for a connection, so nobody thinks unsaved work is saved.
 */
export function AppLayout({ children }: { children: ReactNode }) {
  const { user, signOut } = useSession();
  const navigate = useNavigate();
  const [pending, setPending] = useState(0);
  const [online, setOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  );

  useEffect(() => onPendingChange(setPending), []);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);

  const name = user ? `${user.firstName} ${user.lastName}`.trim() : "";

  return (
    <div className="app-column">
      <div className="flex items-center justify-between gap-4 border-b border-border pb-3.5 pt-[18px] print:hidden">
        <img
          src="/icons/gateway-mastercard-logo.png"
          alt="Mastercard Foundation GATEWAY"
          className="h-11 w-auto"
        />
        <img src="/icons/cchub-logo.png" alt="CcHUB" className="h-[26px] w-auto" />
      </div>
      <div className="flex items-center justify-between border-b border-border py-3 print:hidden">
        <div className="font-[family-name:var(--font-display)] text-[15px] font-extrabold text-accent">
          Readiness Engine
          <small className="block font-[family-name:var(--font-body)] text-[10px] font-semibold uppercase tracking-[0.06em] text-text-muted">
            GATEWAY Growth Pathway
          </small>
        </div>
        {user ? (
          <div className="text-right text-[12px] text-text-muted">
            <b className="block text-[12.5px] text-text">{name || user.email}</b>
            {user.participantId ? <span className="block">ID {user.participantId}</span> : null}
            <button
              type="button"
              className="text-[11px] text-accent underline"
              onClick={() => {
                signOut();
                void navigate(user.role === "Participant" ? "/" : "/admin", { replace: true });
              }}
            >
              Sign out
            </button>
          </div>
        ) : null}
      </div>
      {!online || pending > 0 ? (
        <div
          role="status"
          className="sticky top-0 z-10 mt-2 rounded-[var(--radius-control)] border border-warning/40 bg-warning-tint px-4 py-2 text-[13px] print:hidden"
        >
          {!online ? <b>You're offline. </b> : null}
          {pending > 0
            ? `${pending} change${pending === 1 ? "" : "s"} saved on this device will be sent when you're back online.`
            : "You can keep writing. Changes save on this device and send when you reconnect."}
        </div>
      ) : null}
      <main className="flex-1 pb-10 pt-[26px]">{children}</main>
      <footer className="flex justify-between border-t border-border py-4 text-[12px] text-text-muted print:hidden">
        <span>GATEWAY Program · 6-Step Growth Pathway</span>
      </footer>
    </div>
  );
}
